import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

// Tour contextual pentru pagini complexe — overlay cu spotlight pe elemente concrete și
// tooltip-uri lângă fiecare. Apare automat prima dată pe pagină (per user) și poate fi
// redeschis manual cu butonul „Reia ghidul" pe pagina respectivă.
//
// Folosire în pagini:
//   const tour = usePageTour({
//     tourId: 'audit-log',
//     steps: [
//       { selector: '[data-tour="quick-chips"]', titleKey: 'tour.audit.chips.title', descKey: '...' },
//       { selector: '[data-tour="verify-btn"]', titleKey: '...', descKey: '...', placement: 'bottom' },
//     ]
//   });
//   return <>{tour.tour}<Button onClick={tour.startTour}>Reia ghidul</Button>...</>
//
// Persistență: localStorage.setItem(`tourSeen_${tourId}_${userId}`, 'true').
// Ăsta e tracking client-side (per browser, per user) — diferit de onboarding wizardul global.
// Dacă userul își schimbă browserul/PC-ul, tour-ul reapare. E intenționat: tour-urile sunt scurte,
// nu deranjează la o re-vizualizare ocazională.

const TOOLTIP_W = 340;
const TOOLTIP_MIN_H = 160;
const PADDING = 8;

const TourOverlay = ({ step, stepIndex, totalSteps, onNext, onPrev, onClose }) => {
  const { t } = useTranslation();
  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);

  const recalc = useCallback(() => {
    if (!step?.selector) return;
    const el = document.querySelector(step.selector);
    if (!el) {
      // Selectorul nu mai e în DOM (pagina e altfel acum) — închidem tour-ul.
      onClose();
      return;
    }
    const r = el.getBoundingClientRect();
    setRect(r);

    // Auto-scroll dacă elementul nu e vizibil
    const inView = r.top >= 0 && r.bottom <= window.innerHeight;
    if (!inView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Calc poziție tooltip — folosim clientHeight real dacă e disponibil
    const tH = tooltipRef.current?.offsetHeight || TOOLTIP_MIN_H;
    const placement = step.placement || 'bottom';
    let top, left;
    if (placement === 'bottom') {
      top = r.bottom + 12;
      left = r.left + r.width / 2 - TOOLTIP_W / 2;
      if (top + tH > window.innerHeight - PADDING) {
        // dacă nu încape jos, încearcă sus
        top = r.top - tH - 12;
      }
    } else if (placement === 'top') {
      top = r.top - tH - 12;
      left = r.left + r.width / 2 - TOOLTIP_W / 2;
      if (top < PADDING) top = r.bottom + 12;
    } else if (placement === 'right') {
      top = r.top + r.height / 2 - tH / 2;
      left = r.right + 12;
    } else if (placement === 'left') {
      top = r.top + r.height / 2 - tH / 2;
      left = r.left - TOOLTIP_W - 12;
    }
    // Constrânge în viewport
    left = Math.max(PADDING, Math.min(left, window.innerWidth - TOOLTIP_W - PADDING));
    top = Math.max(PADDING, Math.min(top, window.innerHeight - tH - PADDING));
    setTooltipPos({ top, left });
  }, [step, onClose]);

  useLayoutEffect(() => {
    recalc();
  }, [recalc]);

  useEffect(() => {
    const onResize = () => recalc();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [recalc]);

  // Esc → close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!rect) return null;

  const cutout = {
    top: Math.max(0, rect.top - 4),
    left: Math.max(0, rect.left - 4),
    width: rect.width + 8,
    height: rect.height + 8,
  };

  return (
    <>
      {/* Overlay cu 4 dreptunghiuri care lasă cutout în jurul elementului highlight-uit */}
      <div className="fixed inset-0 z-[80] pointer-events-none">
        <div
          className="absolute bg-slate-900/70 transition-all duration-200"
          style={{ left: 0, right: 0, top: 0, height: cutout.top }}
        />
        <div
          className="absolute bg-slate-900/70 transition-all duration-200"
          style={{ left: 0, right: 0, top: cutout.top + cutout.height, bottom: 0 }}
        />
        <div
          className="absolute bg-slate-900/70 transition-all duration-200"
          style={{ top: cutout.top, left: 0, width: cutout.left, height: cutout.height }}
        />
        <div
          className="absolute bg-slate-900/70 transition-all duration-200"
          style={{
            top: cutout.top,
            left: cutout.left + cutout.width,
            right: 0,
            height: cutout.height,
          }}
        />
        {/* Highlight border în jurul cutout-ului */}
        <div
          className="absolute border-2 border-primary-500 rounded-md transition-all duration-200 shadow-[0_0_0_2px_rgba(59,130,246,0.25)]"
          style={{ top: cutout.top, left: cutout.left, width: cutout.width, height: cutout.height }}
        />
      </div>

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-[81] bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-4 pointer-events-auto"
        style={{ top: tooltipPos.top, left: tooltipPos.left, width: TOOLTIP_W }}
      >
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-1">
          {t('onboarding.progress.stepCount', { current: stepIndex + 1, total: totalSteps })}
        </p>
        <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">
          {t(step.titleKey)}
        </h4>
        <p className="text-xs text-slate-600 dark:text-slate-300 mb-3 leading-relaxed">
          {t(step.descKey)}
        </p>
        <div className="flex justify-between items-center gap-2">
          <button
            onClick={onClose}
            className="text-[11px] text-slate-500 dark:text-slate-400 underline hover:text-slate-700 dark:hover:text-slate-200"
          >
            {t('onboarding.controls.skip')}
          </button>
          <div className="flex gap-2">
            {stepIndex > 0 && (
              <button
                onClick={onPrev}
                className="text-xs px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                {t('onboarding.controls.prev')}
              </button>
            )}
            <button
              onClick={onNext}
              className="text-xs px-3 py-1.5 rounded-md bg-primary-600 hover:bg-primary-700 text-white font-medium"
            >
              {stepIndex === totalSteps - 1
                ? t('onboarding.controls.finish')
                : t('onboarding.controls.next')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Hook reusable per pagină. autoStart=true → tour-ul pornește singur prima dată; ulterior poate fi
// redeschis manual cu funcția returnată `startTour()`.
export const usePageTour = ({ tourId, steps, autoStart = true, delay = 600 }) => {
  const { user } = useAuth();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // #21: ghidul de pagină (tururile contextuale) poate fi dezactivat per firmă. Default activ.
  const guideEnabled = user?.pageGuideEnabled !== false;
  const storageKey = user?.id ? `tourSeen_${tourId}_${user.id}` : null;

  const startTour = useCallback(() => {
    if (!guideEnabled) return;
    setStepIndex(0);
    setActive(true);
    // Marchez ca „văzut" IMEDIAT, nu la close. Anterior dacă userul navigația mid-tour
    // localStorage rămânea negat → tour-ul reapărea la fiecare reîntoarcere pe pagină.
    if (storageKey) localStorage.setItem(storageKey, 'true');
  }, [guideEnabled, storageKey]);

  useEffect(() => {
    if (!autoStart || !storageKey || !guideEnabled) return;
    if (localStorage.getItem(storageKey) === 'true') return;
    // Nu auto-pornim peste walkthrough-ul de onboarding (acela are prioritate; userul îl
    // termină sau îl închide). Tour-ul contextual rămâne disponibil manual via butonul „Reia".
    try {
      if (sessionStorage.getItem('walkthroughActive') === 'true') return;
    } catch (_) {
      /* private mode */
    }
    const timer = setTimeout(() => {
      setStepIndex(0);
      setActive(true);
      // Marchez ca „văzut" odată ce tour-ul a pornit efectiv (delay-ul a expirat).
      // Așa, chiar dacă userul închide tab-ul sau navigația imediat, nu revine la
      // următoarea vizită. Singura cale de a-l rejuca: butonul „Reia ghidul" pe pagină.
      if (storageKey) localStorage.setItem(storageKey, 'true');
    }, delay);
    return () => clearTimeout(timer);
  }, [storageKey, autoStart, delay, guideEnabled]);

  const close = useCallback(() => {
    setActive(false);
    // Idempotent — în mod normal storageKey e deja setat la start. Păstrăm și aici ca
    // safety net pentru calea manuală (startTour din altă parte) sau race conditions.
    if (storageKey) localStorage.setItem(storageKey, 'true');
  }, [storageKey]);

  const next = useCallback(() => {
    setStepIndex((i) => {
      if (i >= steps.length - 1) {
        close();
        return i;
      }
      return i + 1;
    });
  }, [steps.length, close]);

  const prev = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const tour =
    guideEnabled && active && steps[stepIndex] ? (
      <TourOverlay
        step={steps[stepIndex]}
        stepIndex={stepIndex}
        totalSteps={steps.length}
        onNext={next}
        onPrev={prev}
        onClose={close}
      />
    ) : null;

  return { tour, startTour, isActive: active };
};

export default usePageTour;
